import type { BlockRule } from '@bartizo/shared';
import { useInfiniteQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { getBlockRuleAddresses } from '../api/block-rules.js';
import { formatRelative } from '../lib/time.js';
import { Alert } from '../ui/Alert.js';
import { DataTable, type Column } from '../ui/DataTable.js';
import { Loading } from '../ui/Skeleton.js';
import { describeRule } from './block-rule-text.js';
import { ServerLinks } from './ServerLinks.js';

/**
 * Under a rule's row: what it does, and — for a rule that bans — which addresses it
 * banned over the last 30 days, a page at a time, each a way into the address.
 */

type Banned = Awaited<ReturnType<typeof getBlockRuleAddresses>>['items'][number];

const KIND: Readonly<Record<BlockRule['kind'], string>> = {
  request: 'Request rule: refuses each matching request, bans no one',
  behaviour: 'Behaviour rule: bans an address after repeated matches',
  trigger: 'CrowdSec trigger: bans when a CrowdSec scenario fires',
};

export function RuleDetail({ rule }: { rule: BlockRule }): ReactNode {
  return (
    <div className="stack">
      <p className="text-sm">
        <strong>{KIND[rule.kind]}.</strong> {describeRule(rule)}
        {rule.site !== undefined && ` · only on ${rule.site}`}
      </p>
      {rule.kind === 'request' ? (
        <p className="text-sm muted">
          A request rule answers the request itself (403, or the connection dropped) and bans no
          address, so there is no list of addresses to show.
        </p>
      ) : (
        <RuleAddresses rule={rule} />
      )}
    </div>
  );
}

function RuleAddresses({ rule }: { rule: BlockRule }): ReactNode {
  const key = `${rule.kind}:${rule.id}`;
  const list = useInfiniteQuery({
    queryKey: ['block-rule-addresses', key],
    queryFn: ({ pageParam }) => getBlockRuleAddresses(key, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  if (list.isPending) return <Loading />;
  if (list.isError) {
    return (
      <Alert tone="danger" title="Could not load the addresses">
        {list.error instanceof Error ? list.error.message : 'Try again in a moment.'}
      </Alert>
    );
  }
  const rows = list.data.pages.flatMap((page) => page.items);
  const days = list.data.pages[0]?.days ?? 30;

  const columns: Column<Banned>[] = [
    {
      key: 'ip',
      header: 'Address',
      rowHeader: true,
      sortValue: (row) => row.ip,
      cell: (row) => <Link to={`/addresses/${encodeURIComponent(row.ip)}`}>{row.ip}</Link>,
    },
    {
      key: 'bans',
      header: 'Bans',
      align: 'end',
      sortValue: (row) => row.bans,
      cell: (row) => row.bans,
    },
    {
      key: 'servers',
      header: 'On servers',
      cell: (row) => <ServerLinks servers={row.servers} count={row.serverCount} />,
    },
    {
      key: 'first',
      header: 'First',
      wideOnly: true,
      sortValue: (row) => Date.parse(row.firstSeen),
      cell: (row) => formatRelative(row.firstSeen),
    },
    {
      key: 'last',
      header: 'Last',
      sortValue: (row) => Date.parse(row.lastSeen),
      cell: (row) => formatRelative(row.lastSeen),
    },
  ];

  return (
    <section>
      <h4>Addresses it banned, last {days} days</h4>
      <DataTable
        caption={`Addresses banned by ${rule.name}`}
        columns={columns}
        rows={rows}
        rowKey={(row) => row.ip}
        pagination={{
          sizes: [10, 25, 50],
          hasMore: list.hasNextPage,
          loadingMore: list.isFetchingNextPage,
          onLoadMore: () => {
            void list.fetchNextPage();
          },
        }}
        empty={
          <p className="muted text-sm">
            No server has reported a ban by this rule in {days} days. Until a server runs the rule
            (its helper and agent are up to date), none can.
            {rule.kind === 'behaviour' &&
              rule.statuses.length > 0 &&
              ` It counts only requests answered ${rule.statuses.join(', ')}: a site that answers unknown pages with a redirect or a page never gives it any.`}
          </p>
        }
      />
    </section>
  );
}
